/**
 * Onboarding · Step 5 — Open the chat, then send the pre-filled prompt
 * to generate three product images.
 *
 * Two-phase:
 *
 * Phase A — Onboarding chat not yet open:
 *   Spotlights the chat FAB (`agent-chat-toggle`). Copy tells the user
 *   to open the chat. The FAB routes through onboarding's dedicated
 *   session (`useOnboardingAgentSession` via `activeOnboarding.openAgentChat`).
 *   Advances to phase B when that session becomes the active floating
 *   chat — gated on the session id, not bare "some chat is open", so a
 *   pre-existing unrelated chat can't auto-skip this step or capture the
 *   scripted prompt (Sentry USER-DASHBOARD-2SX).
 *
 * Phase B — Onboarding chat open:
 *   Pins `pinnedComposerPrompt` so the MessageInput hydrates with the
 *   prompt text. Spotlights the send button. Also shows the TIP line.
 *   When the user sends (detected via `cohesive:onboarding:composer-prompt-sent`
 *   with the matching id), kicks off the frontend `seedOnboardingArtifacts`
 *   pipeline and advances to Step 6.
 *
 * The agent receives the same prompt (real flow_session is created) and
 * its reply is ambient chat — onboarding doesn't wait for it.
 */

import { useCanvasApi } from "@/pages/canvas/context/CanvasContext";
import {
  imageColumnPositions,
  seedOnboardingArtifacts,
} from "@/pages/canvas/onboarding/seedOnboardingArtifacts";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useEffect, useRef, type CSSProperties } from "react";
import { useCanvasFloatingChat } from "../../../../context/CanvasFloatingChatContext";
import {
  useCanvasOnboardingState,
  useCanvasOnboardingUpdate,
} from "../../../../context/CanvasOnboardingContext";
import { useCanvasRoute } from "../../../../context/CanvasRouteContext";
import { Flourish, StepHeader } from "../shared/StepHeader";
import type { StepProps } from "../shared/types";

const PINNED_ACTION_ID = "onboarding:gen-images";

/** Pre-hosted AeroCarry product image URLs. */
const SOURCE_IMAGE_URLS = [
  "https://static.dev.cohesive.ai/images/onboarding/AeroCarry_Handle_Detail.jpg",
  "https://static.dev.cohesive.ai/images/onboarding/AeroCarry_Hero_Lounge.jpg",
  "https://static.dev.cohesive.ai/images/onboarding/AeroCarry_Organized_Interior.jpg",
] as const;

const SOURCE_IMAGE_NAMES = [
  "AeroCarry Product Image 1.jpg",
  "AeroCarry Product Image 2.jpg",
  "AeroCarry Product Image 3.jpg",
] as const;

const GEN_IMAGES_MESSAGE =
  "Can you read through this brief and generate some product shots for the AeroCarry suitcase?";

export const GenerateImagesStep = memo(function GenerateImagesStep({
  data,
  onAdvance,
}: StepProps) {
  const update = useCanvasOnboardingUpdate();
  const { highlightEmphasisTick } = useCanvasOnboardingState();
  const { currentSessionId } = useCanvasFloatingChat();

  // Phase A → B is gated on onboarding's *own* session being the active
  // floating chat — not "any chat is open". The session id is minted
  // when the user clicks the spotlighted chat FAB (which routes through
  // `useOnboardingAgentSession`) and persisted onto the
  // node. Until then we hold Phase A so the "Open the chat" step is
  // actually taught, and a pre-existing unrelated chat can't capture the
  // scripted prompt.
  const onboardingSessionId = data.onboardingSessionId;
  const onboardingChatOpen =
    !!onboardingSessionId && currentSessionId === onboardingSessionId;

  // Bump the emphasis tick so OnboardingSpotlight replays its
  // attention pulse. Used when the user clicks the in-card "Click the
  // highlighted prompt" row — in case they missed the subtler steady
  // halo on the send button.
  const emphasizeSendHighlight = () => {
    update({ highlightEmphasisTick: highlightEmphasisTick + 1 });
  };
  const { canvasId } = useCanvasRoute();
  const canvas = useCanvasApi();
  const queryClient = useQueryClient();

  const onAdvanceRef = useRef(onAdvance);
  onAdvanceRef.current = onAdvance;

  const seedTimerRef = useRef<number | null>(null);

  // Phase A → B: when onboarding's chat session becomes active, switch
  // from chat-toggle spotlight to send-button spotlight + pinned prompt.
  useEffect(() => {
    if (!onboardingChatOpen) {
      update({
        lockedViewport: false,
        highlightTargetId: "agent-chat-toggle",
        pinnedComposerPrompt: null,
      });
    } else {
      update({
        lockedViewport: false,
        highlightTargetId: "chat-send-button",
        pinnedComposerPrompt: {
          id: PINNED_ACTION_ID,
          message: GEN_IMAGES_MESSAGE,
        },
      });
    }
    return () => {
      update({
        highlightTargetId: null,
        pinnedComposerPrompt: null,
      });
    };
  }, [onboardingChatOpen, update]);

  // Detect send event then kick off frontend image seeding.
  useEffect(() => {
    const onSent = (e: Event) => {
      const detail = (e as CustomEvent<{ id?: string }>).detail;
      if (detail?.id !== PINNED_ACTION_ID) return;

      // Advance immediately so the UI moves to the next step.
      // Seed images after a short delay so it feels like the agent is
      // working before the nodes appear on the canvas.
      onAdvanceRef.current();

      if (canvasId) {
        // Seed unconditionally — advancing without seeding would strand
        // the next step waiting for image nodes that never land. Fall
        // back to the canvas origin if the brief's drop point was never
        // captured.
        const dropPoint = (data.pdfDropPoint as
          | { x: number; y: number }
          | undefined) ?? {
          x: 0,
          y: 0,
        };
        const positions = imageColumnPositions(dropPoint);
        seedTimerRef.current = window.setTimeout(() => {
          void seedOnboardingArtifacts(canvasId, queryClient, canvas, {
            kind: "image",
            urls: SOURCE_IMAGE_URLS,
            names: SOURCE_IMAGE_NAMES,
            positions,
          });
        }, 5_000);
      }
    };

    window.addEventListener("cohesive:onboarding:composer-prompt-sent", onSent);
    return () => {
      window.removeEventListener(
        "cohesive:onboarding:composer-prompt-sent",
        onSent,
      );
      // Do NOT cancel seedTimerRef here — onAdvanceRef.current() causes this
      // component to unmount, which would fire this cleanup and cancel the
      // timer before it has a chance to run. seedOnboardingArtifacts only
      // captures canvasId/queryClient/canvas which remain valid after unmount.
    };
  }, [canvasId, data.pdfDropPoint, canvas, queryClient]);

  const phase: "chat" | "send" = onboardingChatOpen ? "send" : "chat";

  return (
    <div style={{} as CSSProperties}>
      <StepHeader
        heading={
          <>
            Open the chat. Send the <Flourish>prompt</Flourish>.
          </>
        }
        lede={
          <>
            Your selection comes with you automatically, so the agent already
            has the context it needs.
          </>
        }
      />

      {/* Only the currently needed action is shown — the step swaps
          this single row from "open the chat" to "send the prompt" as
          the user progresses, keeping the card short. */}
      <div
        className="_co_onb_settle_3"
        style={{
          marginTop: "var(--co-onb-stack-md)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--co-onb-stack-xs)",
        }}
      >
        {phase === "chat" ? (
          <StepRow
            done={false}
            label="Open the chat from the bottom right"
            subLabel="Your selection is already attached"
            active
          />
        ) : (
          <StepRow
            done={false}
            label="Send the suggested prompt"
            subLabel="We've drafted one for you in the chat"
            active
            onClick={emphasizeSendHighlight}
          />
        )}
      </div>
    </div>
  );
});

// ─── Subcomponents ────────────────────────────────────────────────────────────

const StepRow = memo(function StepRow({
  done,
  label,
  subLabel,
  active,
  onClick,
}: {
  done: boolean;
  label: string;
  subLabel?: string;
  active: boolean;
  /** Optional — when present, the row renders as a button. Used by the
   *  send-phase row to nudge the spotlight on the chrome send button. */
  onClick?: () => void;
}) {
  const interactive = !!onClick;
  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        background: active ? "var(--co-snap-fill)" : "transparent",
        border: active
          ? "1px dashed var(--co-track-strong)"
          : "1px solid transparent",
        borderRadius: 12,
        opacity: !active && !done ? 0.45 : 1,
        transition: "opacity 320ms ease, background-color 200ms ease",
        textAlign: "left",
        cursor: interactive ? "pointer" : "default",
        userSelect: interactive ? "none" : undefined,
      }}
    >
      <span
        className={active && !done ? "_co_onb_dot" : undefined}
        style={{
          flexShrink: 0,
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: done ? "var(--co-rust)" : "var(--co-rust)",
          display: "inline-block",
        }}
      />
      <span
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: "-0.005em",
            color: "var(--co-fg)",
          }}
        >
          {label}
        </span>
        {subLabel && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 400,
              color: "var(--co-fg-muted)",
              fontFamily:
                "ui-monospace, 'JetBrains Mono', 'SF Mono', Menlo, monospace",
              letterSpacing: "0.04em",
            }}
          >
            {subLabel}
          </span>
        )}
      </span>
    </div>
  );
});
